import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/* eslint arrow-parens: 0 */
/* eslint implicit-arrow-linebreak: 0 */
/* eslint func-names: 0 */

/**
 * React-Select exposes a style hook to add styles via jss and these are not added as inline styles but added as
 * a part of css classes.
 */

import _mapValues from 'lodash/mapValues';
import _curry from 'lodash/curry';
import _isFunction from 'lodash/isFunction';
import _identity from 'lodash/identity';
import _get from 'lodash/get';

// Constants
import { EMPTY_OBJECT } from '@tekion/tekion-base/app.constants';
import COLORS from '@tekion/tekion-styles-next/scss/variables.scss';

// Helpers
import { defaultMemoize } from 'reselect';
import { isOptionNew } from '../helpers/advancedSelect.general';
import { Z_INDEX_MAX } from '../../../constants/style.constants';
var SELECT_BOX_HEIGHT = '3.2rem';
var SELECT_FONT_SIZE = '1.4rem';
var DEFAULT_WIDTH_PERCENT = '25%';
var getBorderColor = function getBorderColor(props) {
  var isFocused = props.isFocused;
  var hasError = _get(props, 'selectProps.hasError');
  if (hasError) {
    return COLORS.mordantRed;
  }
  if (isFocused) {
    return '#6eaaff';
  }
  return COLORS.ashGray;
};
var getControlStyles = function getControlStyles(styles, props) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    minHeight: SELECT_BOX_HEIGHT,
    height: SELECT_BOX_HEIGHT,
    borderColor: getBorderColor(props),
    borderRadius: '0.2rem',
    boxShadow: 'none',
    ':hover': {
      borderColor: '#2982ff'
    },
    fontSize: SELECT_FONT_SIZE
  });
};

// Disabling user select so that placeholder doesn't get selected when right clicked inside select box
var getPlaceholderStyles = function getPlaceholderStyles(styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    fontSize: SELECT_FONT_SIZE,
    color: '#969aa3',
    pointerEvents: 'none',
    userSelect: 'none'
  });
};
var getIndicatorSeparatorStyles = function getIndicatorSeparatorStyles(styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    display: 'none'
  });
};
var getValueContainerStyles = function getValueContainerStyles(styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    padding: '0 0.8rem 0 1.2rem',
    height: '100%'
  });
};
var getMenuStyles = function getMenuStyles(styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    borderRadius: '0.2rem',
    fontSize: SELECT_FONT_SIZE
  });
};
var getMenuPortalStyles = defaultMemoize(function (styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    zIndex: 1000
  });
});
var getMenuMessageStyles = function getMenuMessageStyles(styles) {
  return _objectSpread(_objectSpread(_objectSpread({}, styles), getPlaceholderStyles(styles)), {}, {
    textAlign: 'left'
  });
};
var getOptionStyles = function getOptionStyles(styles, _ref) {
  var _ref$data = _ref.data,
    data = _ref$data === void 0 ? EMPTY_OBJECT : _ref$data;
  var isAddNewOption = isOptionNew(data);
  var baseStyles = _objectSpread(_objectSpread({}, styles), {}, {
    padding: '0.5rem 1.2rem'
  });
  if (isAddNewOption) {
    baseStyles.color = '#4285F4';
    baseStyles.fontWeight = 600;
  }
  return baseStyles;
};
var getDropdownIndicatorStyles = function getDropdownIndicatorStyles(styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    padding: '0.5rem',
    '> svg': {
      width: '2rem',
      height: '2rem'
    }
  });
};
var getClearIndicatorStyles = function getClearIndicatorStyles(styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    padding: '0.5rem'
  });
};

// React select is using auto-size input which internally sets the width depending upon the text size
// So added !important to take up the entire width
var getInputStyles = function getInputStyles(styles) {
  return _objectSpread(_objectSpread({}, styles), {}, {
    flex: '1 1 auto',
    '> div': {
      width: '100%'
    },
    '& input': {
      width: '100% !important'
    }
  });
};
export var getSingleValueStyles = function getSingleValueStyles(style) {
  return _objectSpread(_objectSpread({}, style), {}, {
    /*
     * 0.8rem comes from the original definition in react-select,
     * maxWidth: `calc(100% - ${spacing.baseUnit * 2}px)`,
     * where spacing.baseUnit is given the default value of 4 (px implied)
     */
    maxWidth: 'calc(100% - 0.8rem)',
    marginLeft: '0.2rem',
    marginRight: '0.2rem'
  });
};
export var getInputStylesInRem = function getInputStylesInRem(style) {
  return _objectSpread(_objectSpread({}, style), {}, {
    margin: '0.2rem',
    paddingBottom: '0.2rem',
    paddingTop: '0.2rem'
  });
};
var SELECT_STYLES = {
  control: getControlStyles,
  placeholder: getPlaceholderStyles,
  indicatorSeparator: getIndicatorSeparatorStyles,
  valueContainer: getValueContainerStyles,
  menu: getMenuStyles,
  menuPortal: getMenuPortalStyles,
  loadingMessage: getMenuMessageStyles,
  noOptionsMessage: getMenuMessageStyles,
  option: getOptionStyles,
  dropdownIndicator: getDropdownIndicatorStyles,
  clearIndicator: getClearIndicatorStyles,
  input: getInputStyles,
  singleValue: _identity,
  menuList: _identity,
  group: _identity,
  groupHeading: _identity,
  multiValue: _identity
};
var getStyleForElement = _curry(function (overrideStyles, defaultStyle, key) {
  return function (styles, params) {
    var elementOverrideStyle = overrideStyles[key];
    var baseStyles = defaultStyle(styles, params);
    if (_isFunction(elementOverrideStyle)) {
      var overrides = elementOverrideStyle(styles, params);
      return _objectSpread(_objectSpread({}, baseStyles), overrides);
    }
    return baseStyles;
  };
});
export var getSelectStyles = function getSelectStyles() {
  var selectOverrides = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  return _mapValues(SELECT_STYLES, getStyleForElement(selectOverrides));
};
export var getOverlayStylesWithCustomMinWidth = function getOverlayStylesWithCustomMinWidth() {
  var width = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_WIDTH_PERCENT;
  return {
    menuPortal: function menuPortal(style) {
      return _objectSpread(_objectSpread({}, style), {}, {
        zIndex: Z_INDEX_MAX,
        minWidth: width
      });
    }
  };
};
export default SELECT_STYLES;